// even though we're lazy loading the babylonjs modules, purely type imports are removed at runtime,
// so no impact on bundle size :) https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-8.html
import type {Engine, Scene} from '@babylonjs/core';
import {IconButton} from '@wandb/components';
import * as React from 'react';
import {useEffect, useRef} from 'react';

import {envIsIntegration} from '../config';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {type Media3DProps} from './PointCloud';
import {InstrumentedCenteredWaveLoader} from './utility/InstrumentedLoader';

// Copied from BabylonJS docs https://doc.babylonjs.com/features/featuresDeepDive/babylonViewer/react/
interface HTML3DElementAttributes extends React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
> {
  source?: string;
  environment?: string;
}

declare module 'react' {
  interface JSX {
    IntrinsicElements: {
      'babylon-viewer': HTML3DElementAttributes;
    };
  }
}

const isBabylonSceneUrl = (sourceUrl: string): boolean => {
  try {
    return new URL(sourceUrl, window.location.href).pathname
      .toLowerCase()
      .endsWith('.babylon');
  } catch {
    return sourceUrl.toLowerCase().endsWith('.babylon');
  }
};

export const Viewer3D: React.FunctionComponent<Media3DProps> = props => {
  const {
    url,
    width,
    height,
    controls,
    backgroundColor,
    tileMedia,
    tileMediaSpec,
    getMediaTracking,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const handleFullscreen = async () => {
    if (containerRef.current) {
      try {
        await containerRef.current.requestFullscreen();
      } catch (e) {
        console.error('Failed to enter fullscreen:', e);
      }
    }
  };

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  // These callbacks are fire-and-forget analytics emitters; their identity
  // is not a signal that the scene should be re-initialized. Keeping them in
  // the effect deps creates a feedback loop: a failed load fires trackError,
  // which mutates the panel tracking context, which re-memoizes these
  // callbacks, which re-runs the effect, which fails again, etc. Stash the
  // latest values in refs and call through them inside the effect.
  const trackSuccessRef = useRef(trackSuccess);
  const trackErrorRef = useRef(trackError);
  // oxlint-disable-next-line react/refs
  trackSuccessRef.current = trackSuccess;
  // oxlint-disable-next-line react/refs
  trackErrorRef.current = trackError;

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }

    let engine: Engine;
    let scene: Scene;
    let cleanup: (() => void) | undefined;

    const initScene = async () => {
      setIsLoading(true);
      try {
        // Load only the specific modules we need
        const {Vector3} = await import('@babylonjs/core/Maths/math.vector');
        const {ArcRotateCamera} =
          await import('@babylonjs/core/Cameras/arcRotateCamera');
        const {HemisphericLight} =
          await import('@babylonjs/core/Lights/hemisphericLight');
        const {SceneLoader} =
          await import('@babylonjs/core/Loading/sceneLoader');

        const {registerBuiltInLoaders} =
          await import('@babylonjs/loaders/dynamic');
        registerBuiltInLoaders();

        // 1. registerBuiltInLoaders() does not include the native .babylon
        //    plugin, so .babylon URLs need babylonFileLoader:
        // https://github.com/BabylonJS/Babylon.js/blob/11ab51f7/packages/dev/loaders/src/dynamic.ts#L17-L65
        // 2. Material.Parse defaults serialized materials to
        //    BABYLON.StandardMaterial and instantiates it by name, so
        //    standardMaterial must be imported for its registration side effect:
        // https://github.com/BabylonJS/Babylon.js/blob/ab6e74cd26a129ceb17180146cb3972a14be6cbe/packages/dev/core/src/Materials/material.ts#L2085-L2094
        if (isBabylonSceneUrl(url)) {
          await Promise.all([
            import('@babylonjs/core/Loading/Plugins/babylonFileLoader'),
            import('@babylonjs/core/Materials/standardMaterial'),
          ]);
        }

        const {Engine} = await import('@babylonjs/core/Engines/engine');
        const {Scene} = await import('@babylonjs/core/scene');
        const {Color4} = await import('@babylonjs/core/Maths/math.color');

        engine = new Engine(canvasRef.current, true);
        // Browsers cap WebGL contexts per page (~16 in Chrome). When a panel
        // renders more 3D tiles than that, the overflow tiles get their
        // context killed and silently render the browser's "broken canvas"
        // fallback — Babylon's load and render paths don't throw for this.
        // Two paths to detect it:
        //   1. Context already lost when we get it. The contextlost event
        //      fired before we could subscribe, so the observable never sees
        //      it. Check synchronously and bail through the outer catch.
        //   2. Context lost later, after we subscribe. Handled by the
        //      onContextLostObservable below.
        if (canvasRef.current) {
          const gl =
            canvasRef.current.getContext('webgl2') ??
            canvasRef.current.getContext('webgl');
          if (gl?.isContextLost()) {
            throw new Error(
              `WebGL context already lost on engine creation (page-level context limit exceeded) for ${url}`
            );
          }
        }
        engine.onContextLostObservable.add(() => {
          console.warn('Babylon WebGL context lost for', url);
          trackErrorRef.current?.();
          engine.stopRenderLoop();
        });
        scene = new Scene(engine);
        if (backgroundColor) {
          scene.clearColor = new Color4(
            backgroundColor.r / 255,
            backgroundColor.g / 255,
            backgroundColor.b / 255,
            backgroundColor.a
          );
        }

        const camera = new ArcRotateCamera(
          'camera',
          0,
          Math.PI / 3,
          10,
          Vector3.Zero(),
          scene
        );
        camera.attachControl(canvasRef.current, true);
        camera.wheelPrecision = 100;
        camera.pinchPrecision = 100;
        camera.upperRadiusLimit = 1000;
        camera.lowerRadiusLimit = 1;
        // don't auto rotate on tests because that'll mess up integration test screenshots
        camera.useAutoRotationBehavior = !envIsIntegration;
        if (camera.autoRotationBehavior) {
          camera.autoRotationBehavior.idleRotationSpeed = 0.5;
          camera.autoRotationBehavior.idleRotationWaitTime = 2000;
          camera.autoRotationBehavior.zoomStopsAnimation = false;
        }

        const light = new HemisphericLight(
          'light',
          new Vector3(0, 1, 0),
          scene
        );
        light.intensity = 0.7;

        const {meshes} = await SceneLoader.ImportMeshAsync('', url, '', scene);
        camera.zoomOn(meshes);
        // Only once the scene is loaded and the meshes are zoomed in on should we consider
        // that the 3D viewer is loaded and ready for screenshotting
        setIsLoading(false);
        trackSuccessRef.current?.();

        engine.runRenderLoop(() => {
          scene.render();
        });

        const handleResize = () => {
          engine.resize();
        };

        const handleFullscreenChange = () => {
          setIsFullscreen(document.fullscreenElement === containerRef.current);
          engine.resize();
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        cleanup = () => {
          window.removeEventListener('resize', handleResize);
          document.removeEventListener(
            'fullscreenchange',
            handleFullscreenChange
          );
          engine.dispose();
        };
      } catch (error) {
        console.error('Error initializing Babylon.js:', error);
        trackErrorRef.current?.();
        setIsLoading(false);
      }
    };

    initScene();
    return cleanup;
  }, [url, backgroundColor]);

  return (
    <div
      ref={containerRef}
      className={`relative ${
        isFullscreen ? 'h-screen w-screen' : 'inline-block'
      }`}>
      <canvas
        data-test={`${
          // oxlint-disable-next-line react/refs
          canvasRef.current != null && !isLoading ? 'loaded' : 'loading'
        }-canvas`}
        ref={canvasRef}
        className={isFullscreen ? 'h-full w-full' : ''}
        style={!isFullscreen ? {width, height} : undefined}
      />
      {isLoading && (
        <InstrumentedCenteredWaveLoader name="3d-viewer" size="small" />
      )}
      {controls && (
        <div className="absolute right-2 top-2 z-10 text-wb-text-secondary hover:text-white [&_button]:!bg-transparent">
          <IconButton
            aria-label="Open 3D viewer fullscreen"
            icon="full-screen-mode-expand"
            iconColor="currentcolor"
            onPress={handleFullscreen}
            variant="ghost"
          />
        </div>
      )}
    </div>
  );
};

export default Viewer3D;
